/** Vendor bookings as the planning API (ans-backend routes/planning/vendorBookingsKal.ts) sends them. Money stays a decimal string. */

export const BOOKING_STATUSES = ['prospecting', 'shortlisted', 'negotiating', 'booked', 'contract-signed', 'cancelled'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export interface BookingLine {
  id: string;
  vendor_booking_id: string;
  event_id: string | null;
  description: string;
  quantity: string;
  /** null for someone who may not see money. */
  unit_price: string | null;
  total: string | null;
  sort_order: number;
}

export interface BookingRow {
  id: string;
  project_id: string;
  vendor_id: string;
  vendor_name: string | null;
  status: BookingStatus | null;
  event_id: string | null;
  event_name: string | null;
  booking_date: string | null;
  confirmation_ref: string | null;
  contract_signed: boolean;
  contract_date: string | null;
  contract_link: string | null;
  special_terms: string | null;
  total_amount: string | null;
  deposit_percent: number | null;
  deposit_due_date: string | null;
  balance_due_date: string | null;
  last_contact_date: string | null;
  next_followup_date: string | null;
  contact_method: string | null;
  contact_notes: string | null;
  quote_id: string | null;
  quote_status: string | null;
  contingency_amount: string | null;
  currency: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  archived: boolean;
  /** Derived from completed payments; null for someone who may not see money. */
  total_paid: string | null;
  deposit_paid: boolean | null;
  balance_paid: boolean | null;
  line_count: number;
  budget_item_id: string | null;
}

export interface BookingDetail extends BookingRow {
  lines: BookingLine[];
  budget_item: { id: string; category_id: string; service: string | null; vendor_name: string | null } | null;
}

/** The fields PATCH accepts (anything else is refused by the API). */
export interface BookingPatch {
  status?: BookingStatus;
  contract_signed?: boolean;
  event_id?: string | null;
  booking_date?: string | null;
  confirmation_ref?: string | null;
  contract_date?: string | null;
  contract_link?: string | null;
  special_terms?: string | null;
  deposit_percent?: number;
  deposit_due_date?: string | null;
  balance_due_date?: string | null;
  last_contact_date?: string | null;
  next_followup_date?: string | null;
  contact_method?: string | null;
  contact_notes?: string | null;
  contingency_amount?: string | null;
  total_amount?: string;
}

export interface BookingLineInput {
  description?: string;
  quantity?: string;
  unit_price?: string;
  event_id?: string | null;
}

/** A vendor quote as the quote picker needs it. */
export interface QuoteChoice {
  id: string;
  vendor_id: string | null;
  vendor_name: string | null;
  status: string;
  total_amount: string | null;
  currency: string | null;
  date_received: string | null;
  archived: boolean;
}
